import React, { useState } from "react";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  MessageCircle,
  ShieldCheck,
  CheckCircle,
  Send,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  AlertCircle,
  Youtube,
} from "lucide-react";
import { Helmet } from "react-helmet";
import { Link } from "lucide-react";
import logo from "../assets/images/logo.png";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const services = [
    "Carpet Cleaning",
    "End Of Lease Cleaning",
    "Commercial Cleaning",
    "Water Damage Restoration",
    "Upholstery Cleaning",
    "Window Cleaning",
    "Mattress Cleaning",
    "Other Service",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(""); // Clear error when user types
  };

  const formatPhoneNumber = (phone) => {
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, "");

    // If it starts with 0 (like 0420), convert to +61 format
    if (digitsOnly.startsWith("0")) {
      return "61" + digitsOnly.substring(1);
    }

    // If it starts with 61, keep it
    if (digitsOnly.startsWith("61")) {
      return digitsOnly;
    }

    // If it starts with +61, remove the +
    if (digitsOnly.startsWith("61")) {
      return digitsOnly;
    }

    // For other formats, assume it's already in international format
    return digitsOnly;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      // Format the form data for WhatsApp
      const whatsappMessage = `
*📞 NEW CONTACT FORM SUBMISSION - Knight Cleanings*
━━━━━━━━━━━━━━━━━━━━━━━
👤 *Name:* ${formData.name}
📧 *Email:* ${formData.email}
📱 *Phone:* ${formData.phone}
📍 *Address:* ${formData.address || "Not provided"}
🧹 *Service Interested:* ${formData.service || "Not specified"}
💬 *Message:* ${formData.message}
━━━━━━━━━━━━━━━━━━━━━━━
📝 *Submitted via Website Contact Form*
⏰ *Timestamp:* ${new Date().toLocaleString()}
━━━━━━━━━━━━━━━━━━━━━━━
      `.trim();

      // Encode the message for URL
      const encodedMessage = encodeURIComponent(whatsappMessage);

      // WhatsApp number - Australian format
      // Original: +61 420 499 661
      // WhatsApp format: 61420499661 (no plus sign, no spaces)
      const whatsappNumber = "+61420499661"; // Correct WhatsApp format

      // Create WhatsApp URL
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

      // Test the URL first
      console.log("WhatsApp URL:", whatsappUrl);

      // Open WhatsApp in a new tab
      const newWindow = window.open(whatsappUrl, "_blank");

      if (!newWindow) {
        setError(
          "Popup blocked! Please allow popups for this site or click the WhatsApp button below.",
        );
        throw new Error("Popup blocked");
      }

      // Simulate submission delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setIsSubmitting(false);
      setIsSubmitted(true);
      setTimeout(() => setIsSubmitted(false), 5000);
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        service: "",
        message: "",
      });
    } catch (err) {
      setIsSubmitting(false);
      // Don't set error if it's just a popup block
      if (!err.message.includes("Popup")) {
        setError(
          "Unable to open WhatsApp. Please try again or contact us directly.",
        );
      }
    }
  };

  const openWhatsAppDirectly = () => {
    // Format user's phone number if they entered one
    let userPhone = formData.phone;
    if (userPhone) {
      const formattedPhone = formatPhoneNumber(userPhone);
      const whatsappUrl = `https://wa.me/61420499661?text=Hi! My name is ${encodeURIComponent(
        formData.name,
      )}. ${
        formData.message
          ? encodeURIComponent(`I need: ${formData.message}`)
          : "I need cleaning services."
      }`;
      window.open(whatsappUrl, "_blank");
    } else {
      // Just open WhatsApp to our business number
      window.open("https://wa.me/61420499661", "_blank");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>Contact Knight Cleanings in Melbourne VIC</title>

        <meta
          name="description"
          content="Contact Knight Cleanings to book professional cleaning services in Melbourne VIC or request a fast, no-obligation quote."
        />

        <meta
          name="keywords"
          content="
        Carpet cleaning Melbourne,
        Upholstery cleaning Melbourne,
        End of lease cleaning Melbourne,
        Mattress cleaning Melbourne,
        Professional cleaning Melbourne,

        Carpet cleaning Dandenong,
        Upholstery cleaning Dandenong,
        End of lease cleaning Dandenong,

        Carpet cleaning Hallam,
        Upholstery cleaning Hallam,

        Carpet cleaning Hampton Park,
        Carpet cleaning Clayton,
        Carpet cleaning Keysborough,
        Carpet cleaning Glen Waverley,
        Carpet cleaning Mount Waverley,
        Carpet cleaning Noble Park,

        Carpet cleaning Lynbrook,
        Carpet cleaning Doveton,

        Mattress cleaning Mornington,
        Carpet cleaning Mornington,
        Carpet cleaning Rye,
        Carpet cleaning Rosebud,

        Professional cleaners Melbourne,
        Steam carpet cleaning Melbourne,
        Deep cleaning services Melbourne,
        Residential cleaning Melbourne,
        Commercial cleaning Melbourne
        "
        />

        <meta name="robots" content="index, follow" />
        <meta
          name="author"
          content="Professional Cleaning Services Melbourne"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact Knight Cleaning",
            url: "https://knightcleanings.com.au/contact",
            description:
              "Get in touch with Knight Cleaning for professional cleaning services in Melbourne. Free quotes, 24/7 support, and fast responses.", // Optional but recommended
            mainEntity: {
              "@type": "CleaningService",
              name: "Knight Cleanings",
              telephone: "+61420499661",
              email: "info@knightcleanings.com.au", // Added email
              address: {
                // Added full address
                "@type": "PostalAddress",
                streetAddress: "11 Warath Drive, Dandenong North",
                addressLocality: "Melbourne",
                addressRegion: "VIC",
                postalCode: "3175",
                addressCountry: "AU",
              },
              contactPoint: [
                // Added multiple contact points
                {
                  "@type": "ContactPoint",
                  telephone: "+61420499661",
                  contactType: "customer service",
                  availableLanguage: ["English"],
                  hoursAvailable: "Mo-Su 07:00-19:00",
                },
                {
                  "@type": "ContactPoint",
                  telephone: "+61420499661",
                  contactType: "emergency",
                  availableLanguage: ["English"],
                  hoursAvailable: "Mo-Su 00:00-23:59",
                },
                {
                  "@type": "ContactPoint",
                  contactType: "whatsapp",
                  url: "https://wa.me/61420499661",
                  availableLanguage: ["English"],
                },
              ],
            },
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800 text-white py-16 md:py-20">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Get in{" "}
              <span className="bg-gradient-to-r from-amber-300 to-yellow-300 bg-clip-text text-transparent">
                Touch
              </span>
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4">
              Contact Knight Cleaning in Melbourne VIC
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Ready for a spotless space? Message us directly on WhatsApp for
              instant response.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <MessageCircle size={18} />
                <span className="font-semibold">Direct WhatsApp Support</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Clock size={18} />
                <span className="font-semibold">24/7 Response</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
              <div>
                <p className="text-red-700 font-medium">{error}</p>
                <button
                  onClick={openWhatsAppDirectly}
                  className="mt-2 inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  <MessageCircle size={16} />
                  Open WhatsApp Directly
                </button>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-8">
              {/* Contact Cards */}
              <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-8 border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-8">
                  Contact Information
                </h2>

                <div className="space-y-6">
                  {/* WhatsApp */}
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 flex items-center justify-center flex-shrink-0">
                      <MessageCircle size={20} className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        WhatsApp Direct
                      </h3>
                      <div className="text-xl font-bold text-gray-900 mt-1">
                        +61 420 499 661
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Click below to message us instantly
                      </p>
                      <button
                        onClick={openWhatsAppDirectly}
                        className="mt-3 inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold px-4 py-2 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
                      >
                        <MessageCircle size={16} />
                        Message on WhatsApp
                      </button>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-r from-amber-100 to-yellow-100 flex items-center justify-center flex-shrink-0">
                      <Mail size={20} className="text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email Us</h3>
                      <a
                        href="mailto:info@knightcleanings.com"
                        className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors block mt-1"
                      >
                        info@knightcleanings.com
                      </a>
                      <p className="text-sm text-gray-600 mt-1">
                        Response within 2 business hours
                      </p>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-r from-purple-100 to-violet-100 flex items-center justify-center flex-shrink-0">
                      <Clock size={20} className="text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Business Hours
                      </h3>
                      <div className="mt-1 space-y-1">
                        <p className="text-gray-900 font-medium">
                          Mon-Fri: 8:00 AM - 6:00 PM
                        </p>
                        <p className="text-gray-900 font-medium">
                          Sat-Sun: 9:00 AM - 6:00 PM
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          WhatsApp available 24/7
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* WhatsApp Help */}
                <div className="mt-8 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <MessageCircle size={16} />
                    WhatsApp Help
                  </h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Make sure WhatsApp is installed on your device</li>
                    <li>• Allow pop-ups for this website</li>
                    <li>• If form doesn't work, use the button above</li>
                  </ul>
                </div>

                <div>
                  <Link to="/" className="flex items-center gap-3 mb-6 group">
                    <img
                      src={logo}
                      alt="Knight Cleanings"
                      className="h-16 w-16 rounded-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div>
                      <h2 className="text-2xl font-bold group-hover:text-amber-400 transition-colors">
                        Knight Cleanings
                      </h2>
                      <p className="text-blue-300 text-sm font-medium">
                        PREMIUM CLEANING SOLUTIONS
                      </p>
                    </div>
                  </Link>
                  <p className="text-gray-300 mb-6">
                    Professional cleaning services with over 15 years of
                    experience. We deliver exceptional results for residential
                    and commercial clients across Melbourne.
                  </p>
                  <div className="flex gap-4">
                    <a
                      href="https://www.facebook.com/knightcleanings"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-10 w-10 rounded-full bg-gray-100 hover:bg-blue-600 flex items-center justify-center transition-all duration-300 hover:scale-110"
                      aria-label="Facebook"
                    >
                      <Facebook size={18} />
                    </a>
                    <a
                      href="https://www.instagram.com/knightcleaningsolutions?igsh=b21uaDRicXRvc2p2&utm_source=qr"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-10 w-10 rounded-full bg-gray-100 hover:bg-pink-600 flex items-center justify-center transition-all duration-300 hover:scale-110"
                      aria-label="Instagram"
                    >
                      <Instagram size={18} />
                    </a>
                    <a
                      href="https://www.linkedin.com/company/knight-cleanings/?viewAsMember=true"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-10 w-10 rounded-full bg-gray-100 hover:bg-blue-700 flex items-center justify-center transition-all duration-300 hover:scale-110"
                      aria-label="LinkedIn"
                    >
                      <Linkedin size={18} />
                    </a>
                    <a
                      href="https://www.youtube.com/@KnightCleanings"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-10 w-10 rounded-full bg-gray-100 hover:bg-red-600 flex items-center justify-center transition-all duration-300 hover:scale-110"
                      aria-label="YouTube"
                    >
                      <Youtube size={18} />
                    </a>
                  </div>
                </div>
              </div>

              {/* Stats Card */}
              <div className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-2xl shadow-2xl p-8 text-white">
                <h3 className="text-xl font-bold mb-6">Why Choose Us</h3>
                <div className="space-y-6">
                  {[
                    { value: "Instant", label: "WhatsApp Response" },
                    { value: "98%", label: "Customer Satisfaction" },
                    { value: "24/7", label: "Emergency Service" },
                    { value: "Free", label: "Quotes & Consultations" },
                  ].map((stat) => (
                    <div key={stat.label} className="flex items-center gap-3">
                      <CheckCircle
                        size={20}
                        className="text-amber-300 flex-shrink-0"
                      />
                      <div>
                        <div className="text-2xl font-bold">{stat.value}</div>
                        <div className="text-green-200 text-sm">
                          {stat.label}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 border border-gray-100">
                <div className="max-w-2xl">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">
                        Send Message via WhatsApp
                      </h2>
                      <p className="text-gray-600">
                        Fill the form and we'll open WhatsApp with your details
                        pre-filled.
                      </p>
                    </div>
                    <div className="hidden md:block">
                      <div className="h-12 w-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center animate-pulse">
                        <MessageCircle size={24} className="text-white" />
                      </div>
                    </div>
                  </div>

                  {isSubmitted ? (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 text-center">
                      <CheckCircle
                        size={48}
                        className="text-green-500 mx-auto mb-4"
                      />
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        WhatsApp Ready!
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Your message has been prepared. WhatsApp should open
                        automatically.
                      </p>
                      <p className="text-sm text-gray-500">
                        If WhatsApp didn't open, please check:
                      </p>
                      <ul className="text-sm text-gray-500 mt-2 space-y-1">
                        <li>• Pop-up blockers are disabled</li>
                        <li>• WhatsApp is installed on your device</li>
                        <li>• Or use the WhatsApp button on the left</li>
                      </ul>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                            placeholder="John Smith"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                            placeholder="john@example.com"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Home Address *
                        </label>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                          placeholder="123 Main St, City, State, ZIP"
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Your Phone Number *
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                            placeholder="04XX XXX XXX or +61 XXX XXX XXX"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Enter your phone number for us to contact you
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Service Interested *
                          </label>
                          <select
                            name="service"
                            value={formData.service}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                          >
                            <option value="">Select a service</option>
                            {services.map((service) => (
                              <option key={service} value={service}>
                                {service}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Message *
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all resize-none"
                          placeholder="Tell us about your cleaning needs, property size, and any specific requirements..."
                        />
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white font-bold px-8 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-95 ${
                              isSubmitting
                                ? "opacity-80 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            {isSubmitting ? (
                              <>
                                <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Preparing WhatsApp...
                              </>
                            ) : (
                              <>
                                <MessageCircle size={18} />
                                Send via WhatsApp
                                <Send size={18} />
                              </>
                            )}
                          </button>

                          <button
                            type="button"
                            onClick={openWhatsAppDirectly}
                            className="flex items-center gap-2 border-2 border-green-500 text-green-600 hover:bg-green-50 font-semibold px-6 py-4 rounded-full transition-all duration-300"
                          >
                            <MessageCircle size={18} />
                            WhatsApp Direct
                          </button>
                        </div>

                        <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                          <p className="font-medium mb-1">How it works:</p>
                          <ul className="space-y-1 text-xs">
                            <li>1. Fill out the form above</li>
                            <li>2. Click "Send via WhatsApp"</li>
                            <li>
                              3. WhatsApp will open with your message pre-filled
                            </li>
                            <li>
                              4. Review and send the message to Knight Cleanings
                            </li>
                          </ul>
                        </div>
                      </div>
                    </form>
                  )}
                </div>

                {/* Trust Indicators */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">
                    Why Trust Knight Cleanings
                  </h3>
                  <div className="grid sm:grid-cols-3 gap-6">
                    {[
                      {
                        title: "Certified Experts",
                        desc: "All technicians are certified and background checked",
                      },
                      {
                        title: "Fully Insured",
                        desc: "$2M liability insurance for your peace of mind",
                      },
                      {
                        title: "Green Certified",
                        desc: "Eco-friendly products and sustainable practices",
                      },
                    ].map((item) => (
                      <div
                        key={item.title}
                        className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-100"
                      >
                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 flex items-center justify-center mb-4">
                          <ShieldCheck size={20} className="text-green-600" />
                        </div>
                        <h4 className="font-bold text-gray-900 mb-2">
                          {item.title}
                        </h4>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
